import { Fade } from "@progress/kendo-react-animation";
import {
  Notification,
  NotificationGroup,
} from "@progress/kendo-react-notification";
import {
  AlertTriangle,
  CheckCircle2,
  Info as InfoIcon,
  LucideIcon,
  XCircle,
} from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "@/components/Kendo/Notification/style.scss";

export type Type = "success" | "error" | "info" | "warning";

interface ShowMessageOptions {
  title?: React.ReactNode;
}

interface SnackbarContextType {
  showMessage: (
    message: React.ReactNode,
    type: Type,
    options?: ShowMessageOptions,
  ) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a KendoSnackbarProvider");
  }
  return context;
};

interface SnackbarMessage {
  id: number;
  message: React.ReactNode;
  type: Type;
  title: React.ReactNode;
}

const ICONS: Record<Type, LucideIcon> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: InfoIcon,
};

//@ts-expect-error cypress is not defined in the global scope
const timeoutDuration = window.parent?.Cypress ? 500 : 10000; // 10 seconds

export const KendoSnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);
  const idRef = useRef(0);

  const showMessage = useCallback(
    (message: React.ReactNode, type: Type, options?: ShowMessageOptions) => {
      const title = options?.title ?? t(`notification.defaultTitle.${type}`);
      setMessages(prev => {
        // Remove all previous entries with the same message
        const filtered = prev.filter(msg => msg.message !== message);
        const id = ++idRef.current;
        setTimeout(() => {
          setMessages(current => current.filter(msg => msg.id !== id));
        }, timeoutDuration);
        return [...filtered, { id, message, type, title }];
      });
    },
    [t],
  );

  return (
    <SnackbarContext.Provider value={{ showMessage }}>
      {children}
      <div className="tlx-notification tlx-notification-center">
        <NotificationGroup>
          <Fade>
            {messages.map(msg => {
              const Icon = ICONS[msg.type];
              return (
                <Notification
                  key={msg.id}
                  type={{ style: msg.type, icon: false }}
                  closable={true}
                  className={`tlx-notification-${msg.type}`}
                  onClose={() =>
                    setMessages(prev => prev.filter(m => m.id !== msg.id))
                  }
                >
                  <div className="tlx-notification__accent" aria-hidden />
                  <span
                    className="tlx-notification__icon"
                    data-test-id={`tlx-notification-icon-${msg.type}`}
                  >
                    <Icon size={14} strokeWidth={2.2} aria-hidden />
                  </span>
                  <div className="tlx-notification__body">
                    <div className="tlx-notification__title">{msg.title}</div>
                    {msg.message != null && msg.message !== "" && (
                      <div className="tlx-notification__description">
                        {msg.message}
                      </div>
                    )}
                  </div>
                </Notification>
              );
            })}
          </Fade>
        </NotificationGroup>
      </div>
    </SnackbarContext.Provider>
  );
};
